import React, { useState } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/userService';
import notify from '../../utils/notify';
import { PASSWORD_MIN_LENGTH, NO_EDGE_WHITESPACE_REGEX } from '../../utils/passwordValidation';

// Stable backend contract string (see UserService.CURRENT_PASSWORD_INCORRECT_MESSAGE).
const WRONG_CURRENT_PASSWORD_BACKEND_MESSAGE = 'Current password is incorrect';

const ChangePasswordModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await userService.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      notify.success(t('changePassword.success'));
      form.resetFields();
      onClose();
    } catch (error) {
      const isWrongCurrentPassword =
        error.response?.status === 400 &&
        error.response?.data?.message === WRONG_CURRENT_PASSWORD_BACKEND_MESSAGE;
      if (isWrongCurrentPassword) {
        form.setFields([
          { name: 'currentPassword', errors: [t('changePassword.wrongCurrentPassword')] },
        ]);
      } else {
        notify.error(t('changePassword.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('changePassword.title')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width="min(420px, 95vw)"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="currentPassword"
          label={t('changePassword.currentPassword')}
          rules={[{ required: true, whitespace: true, message: t('changePassword.currentPasswordRequired') }]}
        >
          <Input.Password
            placeholder={t('changePassword.currentPassword')}
            autoComplete="current-password"
            disabled={loading}
            data-testid="change-password-current"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={t('changePassword.newPassword')}
          rules={[
            { required: true, message: t('changePassword.newPasswordRequired') },
            { min: PASSWORD_MIN_LENGTH, message: t('changePassword.minLength') },
            { pattern: NO_EDGE_WHITESPACE_REGEX, message: t('changePassword.noEdgeWhitespace') },
          ]}
        >
          <Input.Password
            placeholder={t('changePassword.newPassword')}
            autoComplete="new-password"
            disabled={loading}
            data-testid="change-password-new"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('changePassword.confirmPassword')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('changePassword.confirmPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('changePassword.mismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('changePassword.confirmPassword')}
            autoComplete="new-password"
            disabled={loading}
            data-testid="change-password-confirm"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            data-testid="change-password-submit"
          >
            {t('changePassword.submit')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
